import { useEffect, createContext, useContext, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
// utils
import { tpcOsAxios } from "../Utils/IntitialAxios";
// Constant
const TPC_OS_JWT_PREFIX = "TPC_OS_JWT";
const JwtStorage = localStorage;

/* --------------------------------- State & Context -------------------------------- */

const initState = {
  initiated: false,
  isSuccess: false,
  errorMessage: null,
  accountInfo: {
    homeAccountId: null,
    isAuthenticated: false,
    username: null,
    roles: [],
    pagesAllow: [],
    displayName: null,
    accessToken: null,
  },
};

const initContext = {
  logout: () => {},
  login: () => {},
  ...initState,
};

const AppAutenticationContext = createContext({ ...initContext });

export const useAppAutenticationContext = () =>
  useContext(AppAutenticationContext);

/* -------------------------------- Component ------------------------------- */

export default function MsalAppAuthenticationProvider({ children }) {
  /* ---------------------------------- Hooks --------------------------------- */
  const { instance, inProgress } = useMsal();
  /* ---------------------------------- State --------------------------------- */
  const [appAuthen, setAppAuthen] = useState({ ...initState });
  const homeActiveAccountId = instance.getActiveAccount()?.homeAccountId;

  /* --------------------- Handler Active Account Changed --------------------- */
  useEffect(() => {
    let ignore = false;

    async function initAuthentication() {
      if (homeActiveAccountId) {
        const activeAccount = instance.getAccountByHomeId(homeActiveAccountId);
        const accessTokenResult = await getTpcOsAccessToken(
          activeAccount,
          instance
        );

        if (!ignore) {
          if (accessTokenResult.success) {
            const accountInfo = getAppAccountInfo(
              activeAccount,
              accessTokenResult
            );
            configAxios(accessTokenResult.accessToken);

            setAppAuthen({
              ...initState,
              initiated: true,
              isSuccess: true,
              errorMessage: null,
              accountInfo: {
                ...accountInfo,
                isAuthenticated: true,
              },
            });
          } else {
            setAppAuthen({
              ...initState,
              initiated: true,
              isSuccess: false,
              errorMessage: accessTokenResult.errorMessage,
            });
          }
        }
      } else {
        if (!ignore) {
          setAppAuthen({
            ...initState,
            initiated: true,
            isSuccess: false,
            errorMessage: "Please login to continue",
          });
        }
      }
    }

    if (inProgress === "none") {
      initAuthentication();
    }

    return () => {
      ignore = true;
    };
  }, [homeActiveAccountId, instance, inProgress]);

  /* ------------------------------ Memo Context ------------------------------ */
  const contextValue = useMemo(() => {
    return {
      ...appAuthen,
      login: () => {},
      logout: () => logout(appAuthen.accountInfo.homeAccountId, instance),
    };
  }, [appAuthen, instance]);

  return (
    <AppAutenticationContext.Provider value={contextValue}>
      {!appAuthen.initiated && <h1>Loading Account Information...</h1>}

      {appAuthen.initiated && appAuthen.isSuccess && children}

      {appAuthen.initiated && !appAuthen.isSuccess && (
        <h1>Authentication error : {appAuthen.errorMessage}</h1>
      )}
    </AppAutenticationContext.Provider>
  );
}

/* --------------------------------- Helepr --------------------------------- */

/* ------------------------- Get Account Information ------------------------ */
function getAppAccountInfo(msalAccountInfo, tokenResult) {
  const accountInfo = {
    username: msalAccountInfo.username,
    displayName: msalAccountInfo.name || msalAccountInfo.username || "Unknow",
    roles: tokenResult.role ? tokenResult.role.split(",") : [],
    pagesAllow: tokenResult.page ? tokenResult.page.split(",") : [],
    accessToken: tokenResult.accessToken,
    homeAccountId: msalAccountInfo.homeAccountId,
  };

  if (msalAccountInfo.idTokenClaims) {
    accountInfo.displayName = msalAccountInfo.idTokenClaims.name;
    accountInfo.username = msalAccountInfo.idTokenClaims.preferred_username;
  }

  if (accountInfo.username) {
    accountInfo.username = accountInfo.username.split("@")[0].toLowerCase();
  }

  return accountInfo;
}

/* -------------------------- Get API Access Token -------------------------- */

async function getTpcOsAccessToken(accountInfo, instance) {
  const storageKey = `${TPC_OS_JWT_PREFIX}_${accountInfo.homeAccountId}`;
  const existValue = JSON.parse(JwtStorage.getItem(storageKey));

  if (
    existValue &&
    existValue.exp &&
    existValue.accessToken &&
    new Date(existValue.exp) > new Date()
  ) {
    return {
      success: true,
      accessToken: existValue.accessToken,
      role: existValue.role,
      page: existValue.page,
    };
  }

  try {
    const tokenResult = await instance.acquireTokenSilent({
      account: accountInfo,
      scopes: ["openid", "profile", "email"],
      forceRefresh: true,
    });

    if (!tokenResult.idToken) {
      return {
        success: false,
        errorMessage: "No IdToken found",
      };
    }

    const result = await tpcOsAxios.post(
      "/oauth2/token",
      new URLSearchParams({
        client_id: "6763d34e-1154-44ed-b45f-a036a108d927",
        client_secret: tokenResult.idToken,
        grant_type: "client_credentials",
      })
    );

    if (result.status === 200) {
      const { data } = result;
      const dateNow = new Date();
      JwtStorage.setItem(
        storageKey,
        JSON.stringify({
          exp:
            dateNow.getTime() +
            data.expires_in * 1000 + // expired in section (UTC time)
            60 * 5 * 1000 + // - 5 min
            dateNow.getTimezoneOffset() * 60 * 1000, // convert to local time
          accessToken: data.access_token,
          role: data.role,
          page: data.page,
        })
      );
      return {
        success: true,
        accessToken: data.access_token,
        role: data.role,
        page: data.page,
      };
    } else {
      let errorMessage = "Error when request accountToken";
      if (result.data) {
        errorMessage = JSON.stringify(result.data);
      }

      return {
        success: false,
        errorMessage,
      };
    }
  } catch (ex) {
    return {
      success: false,
      errorMessage: ex.message || "something went wrong",
    };
  }
}

function configAxios(accessToken) {
  tpcOsAxios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
}

async function logout(homeAccountId, instance) {
  const storageKey = `${TPC_OS_JWT_PREFIX}_${homeAccountId}`;
  JwtStorage.removeItem(storageKey);

  const accountInfo = await instance.getAccountByHomeId(homeAccountId);

  await instance.logoutRedirect({
    logoutHint: accountInfo,
  });
}
